import { db } from '../db/index.js';
import {
  apiClient,
  ApiError,
  getMyEdits,
  getTabExtras,
  postTabEdit,
  postTabRevisions,
  withdrawTabEdit,
  type TabRevisionItem
} from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { newId } from '../utils/id.js';
import { normalizeEditPayload } from '../utils/tabEditPayload.js';
import { nextUploadDelayMs } from '../utils/uploadBackoff.js';
import { isLocalOnlyTab } from '../utils/difficulty.js';
import {
  applyServerTabState,
  dropEdit,
  getQueuedEdits,
  getScoreDraft,
  markEditSent,
  putScoreDraft,
  upsertServerEdits,
  writeTabExtras
} from '../db/tabEdits.js';
import type { LocalTabEdit, TabSong } from '../types/index.js';

export const TAB_EDIT_DROPPED_EVENT = 'fretboard:tab-edit-dropped';

export interface TabEditDroppedDetail {
  tabId: string;
  title?: string;
  reason: 'invalid' | 'disabled' | 'gone' | 'conflict';
}

const G = (typeof window !== 'undefined' ? window : globalThis) as any;
const IN_FLIGHT_KEY = '__fretboard_tabEditSync_inFlight__';
const RERUN_KEY = '__fretboard_tabEditSync_rerun__';
const PUSHING_EDITS_KEY = '__fretboard_pushing_edits__';

const sameAccount = (uid: string, token: string): boolean => {
  const auth = useAuthStore.getState();
  return auth.token === token && auth.user?.id === uid && !auth.isGuest;
};

function emitDropped(
  tabId: string,
  title: string | undefined,
  reason: TabEditDroppedDetail['reason']
): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<TabEditDroppedDetail>(TAB_EDIT_DROPPED_EVENT, {
        detail: { tabId, title, reason }
      })
    );
  }
}

async function restoreScoreDraft(userId: string, edit: LocalTabEdit): Promise<void> {
  const ops = (edit.payload as Record<string, unknown> | null)?.ops;
  const actions = (edit.payload as Record<string, unknown> | null)?.actions;
  const track = (edit.payload as Record<string, unknown> | null)?.track;
  const opsList = Array.isArray(ops) ? ops : [];
  const existing = await getScoreDraft(userId, edit.tabId);
  await putScoreDraft({
    ownerId: userId,
    tabId: edit.tabId,
    baseRevision: edit.baseRevision,
    actions: Array.isArray(actions) ? actions : opsList,
    cursor: opsList.length,
    track: typeof track === 'number' ? track : (existing?.track ?? 0),
    updatedAt: new Date().toISOString()
  });
}

/**
 * Step 1: Push queued edits (status === 'QUEUED', matching current user).
 */
async function pushQueuedEdits(userId: string, token: string): Promise<void> {
  const isUserVerified = useAuthStore.getState().user?.emailVerified !== false;
  const nowIso = new Date().toISOString();

  const queued = await getQueuedEdits(userId);
  const pushingEdits: Set<string> = (G[PUSHING_EDITS_KEY] ??= new Set<string>());

  for (const edit of queued) {
    if (!sameAccount(userId, token)) return;

    if (edit.nextAttemptAt && edit.nextAttemptAt > nowIso) continue;
    if (edit.blocked === 'unverified' && !isUserVerified) continue;

    const tab = await db.tabs.get(edit.tabId);
    if (tab?.pendingUpload === 1) continue;

    const current = await db.tabEdits.get(edit.id);
    if (!current || current.status !== 'QUEUED') continue;

    if (pushingEdits.has(edit.id)) continue;
    pushingEdits.add(edit.id);

    const title = edit.tabTitle ?? tab?.title ?? 'تبلچر';

    try {
      const res = await postTabEdit(edit.tabId, {
        id: edit.id,
        kind: edit.kind,
        base_revision: edit.baseRevision,
        note: edit.note,
        payload: (normalizeEditPayload(edit.kind, edit.payload) as Record<string, unknown>) ?? {}
      });

      await markEditSent(edit.id, {
        status: res.edit.status,
        appliedRevision: res.edit.appliedRevision,
        reviewedAt: res.edit.reviewedAt
      });

      if (res.applied) {
        await applyServerTabState(edit.tabId, res.tab, { force: true });
        if (edit.kind === 'LYRICS' || edit.kind === 'SCORE') {
          try {
            const extras = await getTabExtras(edit.tabId);
            if (extras) await writeTabExtras(edit.tabId, extras);
          } catch (extrasErr) {
            console.warn('tabEditSync: failed to pull extras after applied edit', extrasErr);
          }
        }
      }

      if (res.replaced) {
        const replacedEdit = await db.tabEdits.get(res.replaced);
        if (replacedEdit) {
          await db.tabEdits.update(res.replaced, {
            status: 'WITHDRAWN',
            updatedAt: new Date().toISOString()
          });
        }
      }
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined;
      const code = err instanceof ApiError ? err.data?.code : undefined;

      if (status === 400 && (code === 'INVALID_EDIT' || code === 'EDIT_NO_CHANGE')) {
        await dropEdit(edit.id);
        if (!(edit.origin === 'migrated' && code === 'EDIT_NO_CHANGE')) {
          emitDropped(edit.tabId, title, 'invalid');
        }
        continue;
      }

      if (status === 403 && code === 'EMAIL_NOT_VERIFIED') {
        await db.tabEdits.update(edit.id, {
          blocked: 'unverified',
          updatedAt: new Date().toISOString()
        });
        continue;
      }

      if (status === 403 && code === 'FEATURE_DISABLED') {
        await dropEdit(edit.id);
        if (edit.kind === 'SCORE') {
          await restoreScoreDraft(userId, edit);
        }
        emitDropped(edit.tabId, title, 'disabled');
        continue;
      }

      if (status === 404 || status === 403) {
        await dropEdit(edit.id);
        emitDropped(edit.tabId, title, 'gone');
        continue;
      }

      if (status === 409 && code === 'EDIT_ID_TAKEN') {
        if ((edit.attempts ?? 0) === 0) {
          const newEditId = newId();
          await db.tabEdits.delete(edit.id);
          await db.tabEdits.put({
            ...edit,
            id: newEditId,
            attempts: 1,
            updatedAt: new Date().toISOString()
          });
        } else {
          await dropEdit(edit.id);
        }
        continue;
      }

      if (status === 409 && (code === 'EDIT_CONFLICT' || code === 'TAB_OPS_LIMIT')) {
        await dropEdit(edit.id);
        if (edit.kind === 'SCORE') {
          await restoreScoreDraft(userId, edit);
        }
        emitDropped(edit.tabId, title, 'conflict');
        continue;
      }

      if (status === 409) {
        await dropEdit(edit.id);
        emitDropped(edit.tabId, title, 'conflict');
        continue;
      }

      if (status === 429) {
        let delay = 60_000;
        if (err instanceof ApiError && typeof err.data?.retryAfter === 'number') {
          delay = err.data.retryAfter * 1000;
        }
        await db.tabEdits.update(edit.id, {
          nextAttemptAt: new Date(Date.now() + delay).toISOString(),
          updatedAt: new Date().toISOString()
        });
        return; // stop the push run
      }

      if (status !== undefined && status >= 400 && status < 500) {
        await dropEdit(edit.id);
        emitDropped(edit.tabId, title, 'invalid');
        continue;
      }

      // Network / 5xx
      const attempts = (edit.attempts ?? 0) + 1;
      const delay = nextUploadDelayMs(attempts);
      await db.tabEdits.update(edit.id, {
        attempts,
        nextAttemptAt: new Date(Date.now() + delay).toISOString(),
        updatedAt: new Date().toISOString()
      });
      return; // stop the push run
    } finally {
      pushingEdits.delete(edit.id);
    }
  }
}

/**
 * Step 2: Push withdrawals (pendingWithdraw === 1).
 */
async function pushWithdrawals(userId: string, token: string): Promise<void> {
  const toWithdraw = await db.tabEdits
    .where('ownerId')
    .equals(userId)
    .filter((e) => e.pendingWithdraw === 1)
    .toArray();

  for (const edit of toWithdraw) {
    if (!sameAccount(userId, token)) return;
    try {
      await withdrawTabEdit(edit.tabId, edit.id);
      await db.tabEdits.update(edit.id, {
        status: 'WITHDRAWN',
        pendingWithdraw: 0,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined;
      const code = err instanceof ApiError ? err.data?.code : undefined;

      if (status === 404) {
        await db.tabEdits.update(edit.id, {
          status: 'WITHDRAWN',
          pendingWithdraw: 0,
          updatedAt: new Date().toISOString()
        });
      } else if (status === 409 && code === 'EDIT_NOT_PENDING') {
        const serverStatus = ((err as ApiError).data as any)?.status ?? 'WITHDRAWN';
        await db.tabEdits.update(edit.id, {
          status: serverStatus as any,
          pendingWithdraw: 0,
          updatedAt: new Date().toISOString()
        });
      } else if (status !== undefined && status >= 400 && status < 500) {
        await db.tabEdits.update(edit.id, {
          pendingWithdraw: 0,
          updatedAt: new Date().toISOString()
        });
      } else {
        // network / 5xx
        return;
      }
    }
  }
}

/**
 * FRE-224: one local row per edit id. Local rows still PENDING (sent, not decided locally) are checked against
 * the server's complete list because the incremental pull can miss them: a known id is upserted (so
 * APPROVED/REJECTED/WITHDRAWN replaces PENDING); an id the server does not know is dropped.
 * QUEUED rows (created offline, not yet sent) are never touched. Recently touched rows are left alone.
 */
async function reconcileLocalPending(userId: string, token: string): Promise<void> {
  const cutoff = new Date(Date.now() - 10_000).toISOString();
  const stale = await db.tabEdits
    .where('[ownerId+status]')
    .equals([userId, 'PENDING'])
    .filter((e) => e.pendingWithdraw !== 1 && e.updatedAt < cutoff)
    .toArray();
  if (stale.length === 0) return;
  if (!sameAccount(userId, token)) return;

  const pending = await getMyEdits({ status: 'PENDING' });
  const pendingIds = new Set((pending?.data ?? []).map((e) => e.id));
  const unresolved = stale.filter((e) => !pendingIds.has(e.id));
  if (unresolved.length === 0) return;

  const all = await getMyEdits({ updated_since: '1970-01-01T00:00:00.000Z' });
  const byId = new Map((all?.data ?? []).map((e) => [e.id, e]));
  const known = unresolved.map((e) => byId.get(e.id)).filter((e): e is NonNullable<typeof e> => Boolean(e));
  if (known.length > 0) await upsertServerEdits(known, userId);
  if (all?.truncated) return;
  for (const e of unresolved) {
    if (byId.has(e.id)) continue;
    const cur = await db.tabEdits.get(e.id);
    if (cur && cur.status === 'PENDING' && cur.updatedAt === e.updatedAt) await dropEdit(e.id);
  }
}

/**
 * Step 3: Pull mine.
 */
async function pullMine(userId: string, token: string): Promise<void> {
  if (!sameAccount(userId, token)) return;
  try {
    const lastPullSetting = await db.settings.get('tabEdits:lastPull');
    const lastPull = typeof lastPullSetting?.value === 'string' ? lastPullSetting.value : undefined;
    const res = await getMyEdits(lastPull ? { updated_since: lastPull } : undefined);
    if (res?.data && res.data.length > 0) {
      await upsertServerEdits(res.data, userId);
      let newest = lastPull;
      for (const item of res.data) {
        if (item.updatedAt && (!newest || item.updatedAt > newest)) {
          newest = item.updatedAt;
        }
      }
      if (newest) {
        await db.settings.put({ key: 'tabEdits:lastPull', value: newest });
      }
    }
    await reconcileLocalPending(userId, token);
  } catch (err) {
    console.warn('tabEditSync: pull mine failed', err);
  }
}

/**
 * Step 4: Refresh extras for server tabs in current user's library.
 */
export async function refreshTabExtras(ids?: string[]): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  const userId = user.id;

  const allTabs = await db.tabs.where('ownerId').equals(userId).toArray();
  const serverTabs = allTabs.filter((t) => !isLocalOnlyTab(t) && !t.removedAt);
  const targetTabs = ids && ids.length > 0
    ? serverTabs.filter((t) => ids.includes(t.id))
    : serverTabs;

  if (targetTabs.length === 0) return;

  const tabIds = targetTabs.map((t) => t.id);
  let updatedCount = 0;

  for (let i = 0; i < tabIds.length; i += 200) {
    if (!sameAccount(userId, token)) return;
    const chunk = tabIds.slice(i, i + 200);
    let res: { data: TabRevisionItem[] } | undefined;
    try {
      res = await postTabRevisions(chunk);
    } catch (err) {
      console.warn('refreshTabExtras: postTabRevisions failed', err);
      return;
    }

    if (!res?.data || !Array.isArray(res.data)) continue;

    for (const revItem of res.data) {
      if (!sameAccount(userId, token)) return;
      const local = await db.tabs.get(revItem.id);
      if (!local) continue;

      if (local.extrasRevision === undefined && revItem.revision === 1 && !revItem.hasLyrics) {
        await db.tabs.update(revItem.id, { extrasRevision: 1 });
        continue;
      }

      if (revItem.revision > (local.extrasRevision ?? 0)) {
        if (updatedCount >= 50) {
          return;
        }
        updatedCount++;

        try {
          const queuedDirect = await db.tabEdits
            .where('tabId')
            .equals(revItem.id)
            .filter((e) => e.status === 'QUEUED' && e.mode === 'direct')
            .toArray();
          const queuedKinds = new Set(queuedDirect.map((e) => e.kind));

          if (revItem.revision > 1) {
            try {
              const serverTab = await apiClient.get<TabSong>(`/api/tabs/${revItem.id}`);
              if (serverTab) {
                await applyServerTabState(revItem.id, serverTab);
              }
            } catch (tabErr) {
              console.warn('refreshTabExtras: getTab failed for', revItem.id, tabErr);
            }
          }

          const extras = await getTabExtras(revItem.id);
          if (extras) {
            const filteredExtras: any = { ...extras };
            if (queuedKinds.has('LYRICS')) {
              delete filteredExtras.lyrics;
            }
            if (queuedKinds.has('SCORE')) {
              delete filteredExtras.scoreEdits;
            }
            await writeTabExtras(revItem.id, filteredExtras);
          }
        } catch (extrasErr) {
          console.warn('refreshTabExtras: failed to refresh tab', revItem.id, extrasErr);
        }
      }
    }
  }
}

async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  const userId = user.id;

  await pushQueuedEdits(userId, token);
  await pushWithdrawals(userId, token);
  await pullMine(userId, token);
  await refreshTabExtras();
}

/**
 * Pushes queued edits and withdrawals, pulls own edit suggestions, and refreshes tab extras.
 * Single in-flight runner with rerun. Guests and offline calls are no-ops.
 */
export function syncTabEdits(): Promise<void> {
  if (G[IN_FLIGHT_KEY]) {
    G[RERUN_KEY] = true;
    return G[IN_FLIGHT_KEY];
  }
  G[IN_FLIGHT_KEY] = (async () => {
    try {
      do {
        G[RERUN_KEY] = false;
        await runOnce();
      } while (G[RERUN_KEY]);
    } finally {
      G[IN_FLIGHT_KEY] = null;
    }
  })();
  return G[IN_FLIGHT_KEY];
}

/** Fire and forget background sync; no-op offline or as a guest. */
export function requestTabEditSync(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  syncTabEdits().catch((err) => {
    console.warn('tabEditSync: background sync failed', err);
  });
}
